import { OktaAuth } from '@okta/okta-auth-js';

const issuer = import.meta.env.VITE_OKTA_ISSUER || 'https://signin.kering.com/oauth2/default';
const clientId = import.meta.env.VITE_OKTA_CLIENT_ID || '';
const redirectUri = import.meta.env.VITE_OKTA_REDIRECT_URI || `${window.location.origin}/login/callback`;

if (!clientId) {
  console.warn('VITE_OKTA_CLIENT_ID is not set. Okta login may fail. Set VITE_OKTA_CLIENT_ID in your .env.development');
}

export const oktaAuth = new OktaAuth({
  issuer,
  clientId,
  redirectUri,
  scopes: ['openid', 'email', 'profile', 'offline_access'],
  pkce: true,
  // Persist tokens so the SDK can call /userinfo with a valid access token
  // autoRenew/autoRemove disattivati: il rinnovo automatico senza refresh token usa un iframe
  // verso signin.kering.com, bloccato dalla CSP (frame-ancestors 'self') e quindi mai risolto.
  // Il rinnovo è gestito esplicitamente da restoreSession().
  tokenManager: {
    storage: 'localStorage',
    autoRenew: false,
    autoRemove: false
  },
  // Configure cookies as fallback and ensure proper storage config
  cookies: {
    secure: window.location.protocol === 'https:',
    sameSite: 'lax'
  }
});

// In DEV only: sometimes the Okta SDK stores the PKCE/state transaction in sessionStorage
// which can be lost if the redirect opens in a different tab or session. To make
// debugging easier, expose a helper that copies likely transaction keys from
// sessionStorage into localStorage (so handleLoginRedirect can still find them).
// Use this only for local debugging.
export const ensureOktaTransactionInLocalStorage = () => {
  if (!import.meta.env.DEV) return;
  try {
    const keys = dumpOktaTransactionKeys();
    for (const k of keys) {
      try {
        const v = sessionStorage.getItem(k);
        if (v && !localStorage.getItem(k)) {
          localStorage.setItem(k, v);
        }
      } catch (e) {
        // ignore read/write errors in constrained environments
      }
    }
  } catch (e) {
    // silent
  }
};

// NOTE: window exposure moved below after dumpOktaTransactionKeys declaration

// DEV helper: compute transaction storage keys OktaAuth may use (useful to inspect sessionStorage)
export const dumpOktaTransactionKeys = (clientIdOverride?: string) => {
  const cid = clientIdOverride || clientId || '';
  const keys = [
    `okta.${cid}.idp`,
    `okta.${cid}.state`,
    `okta.${cid}.transaction`,
    `okta.${cid}.token`,
    `okta-${cid}-transaction`,
    `okta-${cid}-state`
  ];
  return keys;
};

// Helpers

/** Rifiuta se la promise non si risolve entro `ms`: nessuna attesa di auth può restare appesa. */
export const withTimeout = <T>(promise: Promise<T>, ms = 10000): Promise<T> =>
  new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error(`Timeout dopo ${ms}ms`)), ms);
    promise.then(
      (value) => { clearTimeout(timer); resolve(value); },
      (err) => { clearTimeout(timer); reject(err); }
    );
  });

/**
 * Ripristina la sessione dai token salvati senza mai usare l'iframe:
 * - token validi → true
 * - token scaduti ma refresh token presente → rinnovo via XHR (/token) → true
 * - altrimenti, errore o timeout → token rimossi e false
 */
export const restoreSession = async (): Promise<boolean> => {
  try {
    const { accessToken, idToken, refreshToken } = oktaAuth.tokenManager.getTokensSync();
    if (!accessToken || !idToken) {
      oktaAuth.tokenManager.clear();
      return false;
    }

    if (!oktaAuth.tokenManager.hasExpired(accessToken) && !oktaAuth.tokenManager.hasExpired(idToken)) {
      return true;
    }

    // Senza refresh token renewTokens() ripiegherebbe sull'iframe: non chiamarlo.
    if (!refreshToken) {
      oktaAuth.tokenManager.clear();
      return false;
    }

    const renewed = await withTimeout(oktaAuth.token.renewTokens(), 10000);
    oktaAuth.tokenManager.setTokens(renewed);
    return true;
  } catch (error) {
    console.warn('Impossibile ripristinare la sessione Okta:', error);
    oktaAuth.tokenManager.clear();
    return false;
  }
};

let redirectingToLogin = false;

/** Sessione non recuperabile: ricorda la pagina corrente e manda l'utente al login (una sola volta). */
export const redirectToLogin = (): void => {
  if (redirectingToLogin) return;
  const { pathname, search } = window.location;
  if (pathname.startsWith('/login')) return;
  redirectingToLogin = true;
  try {
    sessionStorage.setItem('originalUri', pathname + search);
  } catch { /* storage non disponibile */ }
  oktaAuth.tokenManager.clear();
  window.location.replace('/login');
};

export const getOktaToken = async (): Promise<string | null> => {
  try {
    return await oktaAuth.getAccessToken() ?? null;
  } catch (error) {
    console.error('Error getting access token:', error);
    return null;
  }
};

export const isAuthenticated = async (): Promise<boolean> => {
  try {
    return await oktaAuth.isAuthenticated();
  } catch (error) {
    console.error('Error checking authentication:', error);
    return false;
  }
};

export const logout = async (): Promise<void> => {
  try {
    await oktaAuth.signOut();
  } catch (error) {
    console.error('Error during logout:', error);
  }
};

// DEV: expose helper on window for quick console access (after the helper is declared)
try {
  if (import.meta.env.DEV) {
    (window as any).ensureOktaTransactionInLocalStorage = ensureOktaTransactionInLocalStorage;
    (window as any).oktaAuth = oktaAuth;
    (window as any).dumpOktaTransactionKeys = dumpOktaTransactionKeys;
  }
} catch (e) { /* ignore for non-browser env */ }